import { Link, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useRecommendations, useReferences, useTrackFunnelEvent } from '../hooks/useDiscovery';
import { RecommendationCard } from '../components/RecommendationCard';
import { CategoryFilter } from '../components/CategoryFilter';
import { CityFilter } from '../components/CityFilter';
import { SearchBar } from '../components/SearchBar';

export function Home() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const categoryId = searchParams.get('categoryId');
  const cityId = searchParams.get('cityId');
  const { track } = useTrackFunnelEvent();

  const { categories, cities, loading: refsLoading } = useReferences();
  const { recommendations, loading, loadMoreLoading, error, hasMore, loadMore } = useRecommendations({
    q: q || undefined,
    categoryId: categoryId ?? undefined,
    cityId: cityId ?? undefined,
    limit: 12,
  });

  useEffect(() => {
    track({ type: 'discovery_view', metadata: { referrer: document.referrer || 'direct' } });
  }, [track]);

  const hasFilters = Boolean(q) || Boolean(categoryId) || Boolean(cityId);

  const setSearch = (patch: { q?: string; categoryId?: string | null; cityId?: string | null }) => {
    const next = new URLSearchParams(searchParams);
    if (patch.q !== undefined) {
      if (patch.q) next.set('q', patch.q); else next.delete('q');
    }
    if (patch.categoryId !== undefined) {
      if (patch.categoryId) next.set('categoryId', patch.categoryId); else next.delete('categoryId');
      track({ type: 'filter_category', metadata: { category_id: patch.categoryId, previous_category_id: categoryId } });
    }
    if (patch.cityId !== undefined) {
      if (patch.cityId) next.set('cityId', patch.cityId); else next.delete('cityId');
      track({ type: 'filter_city', metadata: { city_id: patch.cityId, previous_city_id: cityId } });
    }
    setSearchParams(next, { replace: true });
  };

  const clearFilters = () => {
    track({ type: 'clear_filters' });
    setSearchParams({}, { replace: true });
  };

  return (
    <div className="min-h-screen">
      <section className="discovery-hero">
        <div className="container discovery-hero-inner">
          <p className="discovery-kicker">LosRecomendados.co · Directorio</p>
          <h1>Descubre lugares que vale la pena conocer.</h1>
          <p>Recomendaciones, negocios, categorías y ciudades seleccionadas para ayudarte a encontrar qué hacer y dónde ir.</p>
          <div className="discovery-search">
            <SearchBar
              value={q}
              onChange={(value) => setSearch({ q: value })}
              onSubmit={(value) => {
                track({ type: 'search', metadata: { query: value } });
                setSearch({ q: value });
              }}
              placeholder="Buscar lugares, restaurantes, cafés..."
            />
          </div>
        </div>
      </section>

      <section className="container py-8" aria-labelledby="directory-explore-title">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="discovery-section-title">Explora</p>
            <h2 id="directory-explore-title" className="text-2xl font-bold text-gray-900 mt-1">Encuentra por categoría o ciudad</h2>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Link to="/categories" className="btn-secondary flex items-center gap-2"><span>Explorar categorías</span> ↗</Link>
          <Link to="/cities" className="btn-secondary flex items-center gap-2"><span>Explorar ciudades</span> ↗</Link>
        </div>
      </section>

      <section className="container py-8" aria-labelledby="tecnicelu-preview-title">
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-100">
            <div>
              <p className="text-xs font-semibold text-gray-500">TEMPORAL</p>
              <h2 id="tecnicelu-preview-title" className="text-lg font-bold text-gray-900">Tecnicelu</h2>
            </div>
            <a href="https://tecnicelu.store" target="_blank" rel="noreferrer" className="btn-secondary btn-sm">Abrir ↗</a>
          </div>
          <iframe
            title="Tecnicelu Store"
            src="https://tecnicelu.store"
            className="w-full"
            style={{ height: 'min(720px, 75vh)', border: 0, display: 'block', background: '#fff' }}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      </section>

      <section className="discovery-filter-bar py-6 sticky top-16 z-40">
        <div className="container">
          <div className="flex flex-wrap items-center gap-4">
            <CategoryFilter selectedId={categoryId} onChange={(id) => setSearch({ categoryId: id })} categories={categories} loading={refsLoading} />
            <CityFilter selectedId={cityId} onChange={(id) => setSearch({ cityId: id })} cities={cities} loading={refsLoading} />
            <div className="flex-1" />
            {hasFilters && (
              <button type="button" onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-900 flex items-center gap-1">× Limpiar filtros</button>
            )}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="container">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Recomendaciones{hasFilters && <span className="text-gray-400 text-lg font-medium"> · resultados filtrados</span>}</h2>
          </div>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>}

          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="card"><div className="skeleton aspect-video w-full" /><div className="p-4 space-y-3"><div className="skeleton h-6 w-3/4" /><div className="skeleton h-4 w-full" /><div className="skeleton h-4 w-2/3" /></div></div>
              ))}
            </div>
          )}

          {!loading && !error && recommendations.length === 0 && (
            <div className="text-center py-20">
              <h3 className="mt-4 text-xl font-semibold text-gray-900">No hay recomendaciones</h3>
              <p className="mt-2 text-gray-600">Intenta cambiar los filtros o busca con otros términos</p>
              {hasFilters && <button type="button" onClick={clearFilters} className="mt-6 btn-secondary">Limpiar filtros</button>}
            </div>
          )}

          {!loading && !error && recommendations.length > 0 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {recommendations.map((rec) => <RecommendationCard key={rec.id} recommendation={rec} />)}
              </div>
              {hasMore && (
                <div className="mt-10 text-center">
                  {loadMoreLoading ? (
                    <div className="flex justify-center gap-3">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-24 w-40 rounded-xl" />)}</div>
                  ) : (
                    <button type="button" onClick={() => { track({ type: 'load_more' }); loadMore(); }} className="btn-secondary">Cargar más</button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
